import React from "react";
import { difference, intersection, map } from "lodash";
import styled, { css } from "styled-components";
import { Color, FontSize, Radius, content, dominant } from "@glossy";
import { Spacing } from "@glossy/spacings";
import NavbarButton from "@komon/navigation/navbar/button";
import { getAppUrl } from "@linkfluence/konf";
import { App } from "@linkfluence/models/app";
import { Locale } from "@linkfluence/models/locale";
import { BaseProducts, MwProducts, Product } from "@linkfluence/models/product";
import Link from "@ui-kit/utils/link";
import { tr } from "@radarly/i18n";
import { User } from "@radarly/models/user";
import logo_klear from "../../../../../../linkfluence/assets/logos/klear.svg?raw";
import logo from "../../../../../../linkfluence/assets/logos/logo-only.svg?raw";
import logo_meltwater from "../../../../../../linkfluence/assets/logos/meltwater.svg?raw";
import Dropdown from "../dropdown";

interface IProps {
  products: Product[];
  locale: Locale;
  user?: User;
}

const getAppName = (product: Product, locale: Locale) =>
  ({
    [App.Radarly]: tr(locale, `MAIN_MENU::APPS::RADARLY::NAME`),
    [App.Search]: tr(locale, `MAIN_MENU::APPS::SEARCH::NAME`),
    [App.Klear]: tr(locale, `MAIN_MENU::APPS::KLEAR::NAME`),
    [App.MediaRelations]: tr(locale, `MAIN_MENU::APPS::MEDIA_RELATIONS::NAME`),
  }[product]);

const getAppDescription = (product: Product, locale: Locale) =>
  ({
    [App.Radarly]: tr(locale, `MAIN_MENU::APPS::RADARLY::DESCRIPTION`),
    [App.Search]: tr(locale, `MAIN_MENU::APPS::SEARCH::DESCRIPTION`),
    [App.Klear]: tr(locale, `MAIN_MENU::APPS::KLEAR::DESCRIPTION`),
    [App.MediaRelations]: tr(
      locale,
      `MAIN_MENU::APPS::MEDIA_RELATIONS::DESCRIPTION`
    ),
  }[product]);

const getMwAppUrl = (product: Product, locale: Locale, user: User) =>
  ({
    [App.Klear]: tr(locale, `MAIN_MENU::APPS::KLEAR::URL`),
    [App.MediaRelations]: tr(locale, `MAIN_MENU::APPS::MEDIA_RELATIONS::URL`, {
      email: user ? user.email : "",
    }),
  }[product]);

const getSearchUrl = (included) =>
  included
    ? getAppUrl("linkfluence_search")
    : "https://www.linkfluence.com/search";

const formatMaps = (products: Product[], included: boolean) =>
  map(products, (product) => ({
    id: product,
    disabled: product === "linkfluence_search" ? false : !included,
    url:
      product === "linkfluence_search"
        ? getSearchUrl(included)
        : getAppUrl(product),
  }));

const getApps = (products: Product[]) => [
  ...formatMaps(intersection(BaseProducts, products), true),
  ...formatMaps(difference(BaseProducts, products), false),
];

const getMwApp = (product, locale, user) => ({
  id: product,
  url: getMwAppUrl(product, locale, user),
  logo: { klear: logo_klear, media_relations: logo_meltwater }[product],
});

const Apps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Small};
  padding: ${Spacing.Large};
`;

const Item = styled(Link)<{ disabled: boolean }>`
  width: 350px;
  display: flex;
  align-items: center;
  gap: ${Spacing.Medium};
  padding: ${Spacing.MediumLarge};
  cursor: pointer;
  font-size: ${FontSize.Medium};
  border: 1px solid ${Color.Black12};
  border-radius: ${Radius.Small};

  :hover {
    border-color: ${dominant};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

const Logo = styled.div`
  svg {
    height: 36px;
    width: 36px;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.div`
  font-weight: 500;
  line-height: 18px;
  color: ${(props) => content(props, "secondary")};
`;

const Description = styled.div`
  font-size: ${FontSize.Small};
  line-height: 16px;
  color: ${content};
  word-break: break-word;
  white-space: normal;
`;

const More = styled.div`
  line-height: 22px;
  padding: ${Spacing.Micro} 0;
  color: ${content};
`;

const AppItem = ({ product, locale }) => (
  <Item
    key={product.id}
    url={product.disabled ? "" : product.url}
    disabled={product.disabled}
    target="_blank"
  >
    <Logo dangerouslySetInnerHTML={{ __html: product.logo || logo }} />
    <Text>
      <Name>{getAppName(product.id, locale)}</Name>
      <Description>{getAppDescription(product.id, locale)}</Description>
    </Text>
  </Item>
);

const Menu = ({ products, locale, user }: IProps) => (
  <Apps>
    {map(getApps(products), (product) => (
      <AppItem product={product} locale={locale} key={product.id} />
    ))}
    <More>{tr(locale, "MAIN_MENU::APPS::MORE")}</More>
    {map(MwProducts, (_product, i) => (
      <AppItem
        product={getMwApp(_product, locale, user)}
        locale={locale}
        key={i}
      />
    ))}
  </Apps>
);

const KomonNavigationNavbarApps = ({ products, locale, user }: IProps) => (
  <Dropdown
    pullRight
    toggle={<NavbarButton glyph="grid-squares" shape="disc" transparent />}
    menu={<Menu products={products} locale={locale} user={user} />}
  />
);

export default KomonNavigationNavbarApps;
